import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Grid, TextField } from '@mui/material';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { LanguageCode } from '../../data/models/common/localizedLanguage';
import { languageMap } from '../../data/models/common/localizedLanguage';
import { useTranslation } from 'react-i18next';

interface FieldConfig {
  name: string;
  label: string;
  multiline?: boolean;
  rows?: number;
}

interface LanguageFormTabsProps<T extends FieldValues> {
  languageCodes: LanguageCode[];
  control: Control<T>;
  fields: FieldConfig[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
}

export const LanguageFormTabs = <T extends FieldValues>({
  languageCodes,
  control,
  fields,
  activeTab: externalActiveTab,
  onTabChange,
}: LanguageFormTabsProps<T>) => {
  const [internalActiveTab, setInternalActiveTab] = useState(0);

  const activeTab =
    externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (onTabChange) {
      onTabChange(newValue);
    } else {
      setInternalActiveTab(newValue);
    }
  };

  useEffect(() => {
    if (externalActiveTab !== undefined) {
      setInternalActiveTab(externalActiveTab);
    }
  }, [externalActiveTab]);

  const { t } = useTranslation();

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="language tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {languageCodes.map((code) => (
            <Tab key={code} label={t(languageMap[code])} />
          ))}
        </Tabs>
      </Box>
      {languageCodes.map((code, index) => (
        <Box
          key={code}
          role="tabpanel"
          hidden={activeTab !== index}
          id={`language-tabpanel-${index}`}
          aria-labelledby={`language-tab-${index}`}
          sx={{ mt: 2 }}
        >
          {activeTab === index && (
            <Box>
              {fields.map((field) => (
                <Grid item xs={12} key={field.name} sx={{ mb: 2 }}>
                  <Controller
                    name={`${field.name}.${code}` as Path<T>}
                    control={control}
                    render={({
                      field: controllerField,
                      fieldState: { error },
                    }) => (
                      <TextField
                        {...controllerField}
                        key={`${field.name}-${code}`}
                        fullWidth
                        label={`${field.label} (${t(languageMap[code])})`}
                        error={!!error}
                        helperText={error?.message}
                        multiline={field.multiline || false}
                        rows={field.rows || 1}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};
